import React, { useState, useEffect } from 'react';
import { gatePassService } from '../../services/financeService';
import { API_BASE } from '../../utils/apiUtils';
import AttachmentPanel from './AttachmentPanel';
import { materialLabel } from './GatePassFormFields';
import PdfActions from '../common/PdfActions';
import './Financial.css';

export const STATUS_STYLE = {
  DRAFT:      { background: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)' },
  ISSUED:     { background: 'var(--color-info-light)', color: 'var(--color-info)' },
  RECEIVED:   { background: 'var(--color-success-light)', color: 'var(--color-success)' },
  DISPATCHED: { background: 'var(--color-warning-light)', color: 'var(--color-warning)' },
  CANCELLED:  { background: 'var(--color-error-light)', color: 'var(--color-error)' },
};

export const TYPE_STYLE = {
  ENTRY: { background: 'var(--color-success-light)', color: 'var(--color-success)' },
  EXIT:  { background: 'var(--color-error-light)', color: 'var(--color-error)' },
};

export const badge = (text, styleMap) => {
  const s = styleMap[text] || { background: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)' };
  return (
    <span style={{ ...s, padding: '2px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600 }}>
      {text}
    </span>
  );
};

export const fmt = (str) => str ? str.replace(/_/g, ' ') : '—';

export const InlineAlert = ({ msg, color = 'var(--color-error)', bg = 'var(--color-error-light)' }) =>
  msg ? (
    <div style={{ background: bg, border: `1px solid ${color}`, color, borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px' }}>
      {msg}
    </div>
  ) : null;

// Fetches the gate pass PDF blob — used by <PdfActions> for Preview + Download.
const fetchGatePassPdfBlob = async (gp) => {
  const response = await fetch(`${API_BASE}/gate-passes/${gp.id}/pdf`, {
    method: 'GET', credentials: 'include', headers: { Accept: 'application/pdf' }
  });
  if (!response.ok) throw new Error('Failed to load PDF');
  return response.blob();
};

/**
 * The gate pass detail pop-up. Used by the Gate Passes page and by every place a gate pass
 * number is shown elsewhere (batch Sourcing / Dispatch / Linked Docs tabs), so they all open
 * the same thing. onChanged fires after something is edited (e.g. an item price).
 */
const GatePassDetailModal = ({ gatePassId, onClose, onChanged }) => {
  const [selectedPass, setSelectedPass] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [editingPriceItemId, setEditingPriceItemId] = useState(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setDetailLoading(true);
    setSelectedPass(null);
    gatePassService.getById(gatePassId)
      .then(res => { if (!cancelled && res.success) setSelectedPass(res.data); })
      .catch(() => { /* handled by null check */ })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [gatePassId]);

  const startEditPrice = (item) => {
    setEditingPriceItemId(item.id);
    setEditingPriceValue(item.purchasePrice || '');
  };

  const saveEditPrice = async (item) => {
    const price = parseFloat(editingPriceValue);
    if (isNaN(price) || price < 0) { setMessage({ text: 'Enter a valid price', error: true }); return; }
    setSavingPrice(true);
    try {
      await gatePassService.updateItemPrice(selectedPass.id, item.id, price, item.purchaseCurrency || 'PKR');
      setMessage({ text: 'Price updated — carried through to the Purchase Order' });
      setEditingPriceItemId(null);
      const res = await gatePassService.getById(selectedPass.id);
      if (res.success) setSelectedPass(res.data);
      if (onChanged) onChanged();
    } catch (e) {
      setMessage({ text: e.message || 'Failed to update price', error: true });
    } finally {
      setSavingPrice(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '700px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {selectedPass ? selectedPass.gatePassNumber : 'Gate Pass Details'}
            {selectedPass && <span style={{ marginLeft: '10px' }}>{badge(selectedPass.status, STATUS_STYLE)}</span>}
          </h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {selectedPass && (
              <PdfActions
                fetchBlob={() => fetchGatePassPdfBlob(selectedPass)}
                filename={`${selectedPass.gatePassNumber}.pdf`}
                label="PDF"
                className="btn-secondary"
              />
            )}
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {message && <InlineAlert msg={message.text} color={message.error ? undefined : 'var(--color-success)'} bg={message.error ? undefined : 'var(--color-success-light)'} />}

        {detailLoading ? (
          <div className="loading-state">Loading details…</div>
        ) : !selectedPass ? (
          <InlineAlert msg="Failed to load gate pass details" />
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px', marginBottom: '18px' }}>
              {[
                ['Type',          badge(selectedPass.type, TYPE_STYLE)],
                ['Material',      materialLabel(selectedPass.materialType)],
                ['Date',          selectedPass.gatePassDate],
                ['Warehouse',     selectedPass.warehouseName || '—'],
                ['Party Name',    selectedPass.partyName],
                ['Party Type',    selectedPass.partyType],
                ['Vehicle #',     selectedPass.vehicleNumber || '—'],
                ['Container #',   selectedPass.containerNumber || '—'],
                ['Driver Name',   selectedPass.driverName || '—'],
                ['Driver Contact', selectedPass.driverContact || '—'],
                ['Total Qty',     selectedPass.totalQuantity ?? '—'],
                ['Total Weight',  selectedPass.totalWeight ? `${selectedPass.totalWeight} KG` : '—'],
                ['Linked Purchase', selectedPass.linkedPurchaseNumber
                  ? <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>{selectedPass.linkedPurchaseNumber}</span>
                  : (selectedPass.type === 'ENTRY' ? <span style={{ color: 'var(--color-warning)' }}>Not yet created</span> : '—')],
                ['Created By',    selectedPass.createdByName || '—'],
                ['Issued By',     selectedPass.issuedByName  || '—'],
                ['Issued At',     selectedPass.issuedAt ? new Date(selectedPass.issuedAt).toLocaleString() : '—'],
                ['Created At',    selectedPass.createdAt ? new Date(selectedPass.createdAt).toLocaleString() : '—'],
              ].map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', marginBottom: '2px' }}>{label}</div>
                  <div style={{ fontSize: '13px', fontWeight: 500 }}>{value}</div>
                </div>
              ))}
            </div>

            {selectedPass.notes && (
              <div style={{ background: 'var(--color-neutral-50)', border: '1px solid var(--color-border-medium)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px' }}>
                <strong>Notes:</strong> {selectedPass.notes}
              </div>
            )}

            {selectedPass.cancelReason && (
              <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error-border)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', color: 'var(--color-error)' }}>
                <strong>Cancel Reason:</strong> {selectedPass.cancelReason}
              </div>
            )}

            <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--color-neutral-700)' }}>
              Items ({selectedPass.items?.length || 0})
            </h4>
            {selectedPass.items?.length > 0 ? (
              <div className="table-container">
                <table className="data-table" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item Name</th>
                      <th>Code</th>
                      <th>Qty</th>
                      <th>Unit</th>
                      <th>Weight (KG)</th>
                      {selectedPass.type === 'ENTRY' && <><th>Purchase Price</th><th>Curr.</th><th>Vendor PO</th></>}
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPass.items.map((item, i) => (
                      <tr key={item.id}>
                        <td>{i + 1}</td>
                        <td style={{ fontWeight: 500 }}>{item.itemName}</td>
                        <td>{item.itemCode || '—'}</td>
                        <td>{item.quantity}</td>
                        <td>{item.unit}</td>
                        <td>{item.weight || '—'}</td>
                        {selectedPass.type === 'ENTRY' && (
                          <>
                            <td>
                              {editingPriceItemId === item.id ? (
                                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                  <input
                                    type="number" min="0" step="0.01" autoFocus
                                    style={{ width: 90, padding: '3px 6px', fontSize: 12, border: '1px solid var(--color-primary)', borderRadius: 4 }}
                                    value={editingPriceValue}
                                    onChange={e => setEditingPriceValue(e.target.value)}
                                  />
                                  <button type="button" disabled={savingPrice} onClick={() => saveEditPrice(item)}
                                    style={{ background: 'var(--color-success)', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: 11 }}>✓</button>
                                  <button type="button" disabled={savingPrice} onClick={() => setEditingPriceItemId(null)}
                                    style={{ background: 'var(--color-bg-tertiary)', border: 'none', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: 11 }}>✗</button>
                                </div>
                              ) : (
                                <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                  {item.purchasePrice ? Number(item.purchasePrice).toLocaleString() : <em style={{ color: 'var(--color-text-tertiary)' }}>not set</em>}
                                  <button type="button" onClick={() => startEditPrice(item)}
                                    style={{ background: 'none', border: '1px solid var(--color-border-strong)', borderRadius: 4, padding: '1px 6px', cursor: 'pointer', fontSize: 10, color: 'var(--color-text-secondary)' }}>
                                    {item.purchasePrice ? 'edit' : '+ set price'}
                                  </button>
                                </span>
                              )}
                            </td>
                            <td>{item.purchaseCurrency || '—'}</td>
                            <td>{item.vendorPo || '—'}</td>
                          </>
                        )}
                        <td>{item.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: 'var(--color-text-tertiary)', fontSize: '13px' }}>No items recorded.</p>
            )}

            {/* Attachments (weighbridge slips, photos, etc.) */}
            <div style={{ marginTop: '20px', borderTop: '1px solid var(--color-border-medium)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--color-neutral-700)' }}>
                Attachments <span style={{ fontWeight: 400, fontSize: '12px', color: 'var(--color-text-secondary)' }}>(weighbridge slips, photos, etc.)</span>
              </h4>
              <AttachmentPanel entityType="GATE_PASS" entityId={selectedPass.id} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GatePassDetailModal;
